import React, { useState } from 'react';
import { FileBrowser } from '../../../../components/storage';
import { useStorageListing } from '../hooks/useStorageListing';
import { useDropboxAuthUrl, useDropboxAuthToken } from '../hooks/useDropboxOAuth';
import { notifyError } from '../../../../services/notify';

interface DropboxConfig {
  user: string;
  accessToken: string;
}

interface Props {
  config: DropboxConfig;
  onChange: (config: DropboxConfig) => void;
  selectedPath: string | null;
  onPathChange: (path: string) => void;
  disabled: boolean;
}

export function DropboxSource({ config, onChange, selectedPath, onPathChange, disabled }: Props) {
  const [baseDir, setBaseDir] = useState('/');

  const connected = !!config.accessToken;

  const { data, isFetching } = useStorageListing(
    'dropbox',
    { username: config.user, password: '', accessToken: config.accessToken, baseDir },
    connected,
  );

  const getAuthUrl = useDropboxAuthUrl();
  const getAuthToken = useDropboxAuthToken();
  const connecting = getAuthUrl.isPending || getAuthToken.isPending;

  const connect = async () => {
    const authUrl = await getAuthUrl.mutateAsync(undefined);
    const handle = window.open(authUrl);
    if (!handle || handle.closed) {
      notifyError({ title: 'Popup Blocker', description: 'Please disable your popup blocker and try again.', timeout: 5000 });
      return;
    }
    const onMessage = async (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      const { code, state } = event.data ?? {};
      if (!code) return;
      window.removeEventListener('message', onMessage);
      const result = await getAuthToken.mutateAsync({ state, code });
      onChange({ user: result.userId, accessToken: result.accessToken });
    };
    window.addEventListener('message', onMessage);
  };

  const logout = () => {
    const logoutWin = window.open('https://www.dropbox.com/logout');
    setTimeout(() => logoutWin?.close(), 100);
    onChange({ user: '', accessToken: '' });
    setBaseDir('/');
  };

  const navigate = (path: string) => setBaseDir(path || '/');

  const levelUp = () => {
    const parts = baseDir.split('/').filter(Boolean);
    parts.pop();
    setBaseDir('/' + parts.join('/'));
  };

  return (
    <div>
      <form onSubmit={e => { e.preventDefault(); if (!connected) connect(); }} autoComplete="off" noValidate>
        <h4>
          {connected ? (
            <span>
              <i className="fas fa-user" aria-hidden="true" /> {config.user}'s Dropbox{' '}
              <button type="button" className="btn btn-primary btn-xs" onClick={logout} disabled={disabled}>
                <i className="fas fa-sign-out-alt" aria-hidden="true" /> Logout
              </button>
            </span>
          ) : (
            <button type="submit" className="btn btn-primary btn-xs" disabled={disabled || connecting}>
              <i className={`fas ${connecting ? 'fa-spinner fa-spin' : 'fa-bolt'}`} aria-hidden="true" /> Connect
            </button>
          )}
        </h4>
      </form>

      <FileBrowser
        pathLabel={baseDir}
        files={data ?? []}
        loading={isFetching}
        selectedPath={selectedPath}
        onSelect={onPathChange}
        onNavigate={navigate}
        onLevelUp={levelUp}
      />
    </div>
  );
}

export function defaultDropboxConfig(): DropboxConfig {
  return { user: '', accessToken: '' };
}
